import DownloadIcon from "@mui/icons-material/Download";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchPlatformAdyenMargin } from "../../../actions/adyenPlatformMarginActions";
import {
    buildPlatformMarginCsv,
    formatEuroFromCents,
    mapPlatformMarginError,
} from "./adyenPlatformMarginUtils";
import "./adyenPlatformMargin.scss";

/**
 * Default range: current calendar month until today.
 *
 * @returns {{ from: string, to: string }} ISO date strings
 */
function defaultDateRange() {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const to = now.toISOString().slice(0, 10);
    const from = start.toISOString().slice(0, 10);
    return { from, to };
}

const SummaryFigures = memo(function SummaryFigures({ totals }) {
    if (!totals) return null;
    return (
        <Box className="adyen-platform-margin__summary" sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Typography variant="body1">
                Zahlungen: <strong>{totals.paymentCount}</strong>
            </Typography>
            <Typography variant="body1">
                Provision (brutto): <strong>{formatEuroFromCents(totals.provisionBruttoCents)}</strong>
            </Typography>
            <Typography variant="body1">
                Adyen-Abzug FoodGenius: <strong>{formatEuroFromCents(totals.adyenAbzugFoodGeniusCents)}</strong>
            </Typography>
            <Typography variant="body1">
                Reingewinn netto: <strong>{formatEuroFromCents(totals.reingewinnNettoCents)}</strong>
            </Typography>
            {typeof totals.liableControlNetCents === "number" && (
                <Typography variant="body2" color="text.secondary">
                    Kontrolle Liable (Summe captured/booked):{" "}
                    <strong>{formatEuroFromCents(totals.liableControlNetCents)}</strong>
                </Typography>
            )}
        </Box>
    );
});
SummaryFigures.displayName = "SummaryFigures";

/**
 * Admin view: FoodGenius platform margin per customer from archived Adyen accounting reports.
 *
 * @returns {JSX.Element} Page content
 */
const AdyenPlatformMarginPage = () => {
    const dispatch = useDispatch();
    const initialRange = useMemo(() => defaultDateRange(), []);
    const [from, setFrom] = useState(initialRange.from);
    const [to, setTo] = useState(initialRange.to);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const handleLoad = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await dispatch(fetchPlatformAdyenMargin({ from, to }));
            setData(result);
        } catch (err) {
            setData(null);
            setError(mapPlatformMarginError(err));
        } finally {
            setLoading(false);
        }
    }, [dispatch, from, to]);

    useEffect(() => {
        handleLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- initial fetch for default date range only
    }, []);

    const handleExportCsv = useCallback(() => {
        if (!data?.customers?.length) return;
        const csv = buildPlatformMarginCsv(data.customers);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `adyen-plattform-marge_${from}_${to}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [data, from, to]);

    const customers = data?.customers ?? [];

    return (
        <div className="adyen-platform-margin" id="adyen-platform-margin-page">
            <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
                Adyen Plattform-Marge
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reingewinn = Provision (Fixed + Variable Fee) minus Adyen-Processing-Gebühren, die FoodGenius auf
                dem Liable Balance Account trägt. Basis: archivierte Accounting Reports (S3).
            </Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                    <TextField
                        label="Von"
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ "data-testid": "adyen-platform-margin-from" }}
                    />
                    <TextField
                        label="Bis"
                        type="date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ "data-testid": "adyen-platform-margin-to" }}
                    />
                    <Button variant="contained" onClick={handleLoad} disabled={loading} data-testid="adyen-platform-margin-load">
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Auswerten"}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportCsv}
                        disabled={!customers.length}
                        data-testid="adyen-platform-margin-export"
                    >
                        CSV exportieren
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} role="alert">
                    {error}
                </Alert>
            )}

            {data && (
                <>
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Report-Tage im Zeitraum: {data.reportDaysInRange}
                            {data.firstReportDay && data.lastReportDay
                                ? ` (${data.firstReportDay} – ${data.lastReportDay})`
                                : ""}
                            {" · "}
                            PSP-Referenzen: {data.uniquePaymentCount}
                            {data.skippedWithoutMerchant > 0
                                ? ` · ohne Seller split: ${data.skippedWithoutMerchant}`
                                : ""}
                        </Typography>
                        <SummaryFigures totals={data.totals} />
                    </Paper>

                    <TableContainer component={Paper}>
                        <Table size="small" data-testid="adyen-platform-margin-table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Kunde</TableCell>
                                    <TableCell>Balance Account</TableCell>
                                    <TableCell align="right">Zahlungen</TableCell>
                                    <TableCell align="right">Provision</TableCell>
                                    <TableCell align="right">Adyen (FG)</TableCell>
                                    <TableCell align="right">Reingewinn netto</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {customers.map((row) => (
                                    <TableRow key={row.balanceAccountId}>
                                        <TableCell>
                                            {row.customerLabel}
                                            {!row.mapped && (
                                                <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                                    (unmapped)
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{row.balanceAccountId}</TableCell>
                                        <TableCell align="right">{row.paymentCount}</TableCell>
                                        <TableCell align="right">{formatEuroFromCents(row.provisionBruttoCents)}</TableCell>
                                        <TableCell align="right">
                                            {formatEuroFromCents(row.adyenAbzugFoodGeniusCents)}
                                        </TableCell>
                                        <TableCell align="right">{formatEuroFromCents(row.reingewinnNettoCents)}</TableCell>
                                    </TableRow>
                                ))}
                                {!customers.length && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            Keine Zahlungen im Zeitraum.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </div>
    );
};

export default AdyenPlatformMarginPage;
